import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { Calendar, RefreshCw, Archive, PlayCircle, Clock, Users, CheckCircle2, AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'Laikos24@!';

const SchoolManagementContent = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [showClearDatesDialog, setShowClearDatesDialog] = useState(false);
  const [dateForm, setDateForm] = useState({ start_date: '', end_date: '' });
  const [processing, setProcessing] = useState(false);

  const fetchSchools = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/school-management/schools`);
      const data = await response.json();
      setSchools(data.schools || []);
      setCurrentDate(data.current_date || '');
    } catch (error) {
      toast.error('Failed to fetch schools');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleSetDates = async () => {
    if (!selectedSchool || !dateForm.start_date || !dateForm.end_date) {
      toast.error('Please fill in all fields');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/school-management/schools/set-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_id: selectedSchool.id,
          start_date: dateForm.start_date,
          end_date: dateForm.end_date,
          admin_password: ADMIN_PASSWORD
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowDateDialog(false);
        fetchSchools();
      } else {
        toast.error(data.detail || 'Failed to set dates');
      }
    } catch (error) {
      toast.error('Failed to set dates');
    } finally {
      setProcessing(false);
    }
  };

  const handleArchiveNow = async () => {
    if (!selectedSchool) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/school-management/schools/${selectedSchool.id}/archive-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: ADMIN_PASSWORD })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Archived ${data.archived_counts?.enrollments || 0} enrollments, ${data.archived_counts?.exam_results || 0} exam results`);
        setShowArchiveDialog(false);
        fetchSchools();
      } else {
        toast.error(data.detail || 'Failed to archive');
      }
    } catch (error) {
      toast.error('Failed to archive school data');
    } finally {
      setProcessing(false);
    }
  };

  const handleReopenSchool = async () => {
    if (!selectedSchool || !dateForm.start_date || !dateForm.end_date) {
      toast.error('Please fill in all fields');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/school-management/schools/${selectedSchool.id}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_id: selectedSchool.id,
          start_date: dateForm.start_date,
          end_date: dateForm.end_date,
          admin_password: ADMIN_PASSWORD
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowReopenDialog(false);
        fetchSchools();
      } else {
        toast.error(data.detail || 'Failed to reopen school');
      }
    } catch (error) {
      toast.error('Failed to reopen school');
    } finally {
      setProcessing(false);
    }
  };

  const handleClearDates = async () => {
    if (!selectedSchool) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/school-management/schools/${selectedSchool.id}/clear-dates`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: ADMIN_PASSWORD })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowClearDatesDialog(false);
        fetchSchools();
      } else {
        toast.error(data.detail || 'Failed to clear dates');
      }
    } catch (error) {
      toast.error('Failed to clear dates');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
      case 'completed':
        return <Badge className="bg-gray-500 hover:bg-gray-600"><Archive className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'upcoming':
        return <Badge className="bg-blue-500 hover:bg-blue-600"><Clock className="w-3 h-3 mr-1" />Upcoming</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const openSetDatesDialog = (school) => {
    setSelectedSchool(school);
    setDateForm({
      start_date: school.start_date || '',
      end_date: school.end_date || ''
    });
    setShowDateDialog(true);
  };

  const openArchiveDialog = (school) => {
    setSelectedSchool(school);
    setShowArchiveDialog(true);
  };

  const openReopenDialog = (school) => {
    setSelectedSchool(school);
    setDateForm({ start_date: '', end_date: '' });
    setShowReopenDialog(true);
  };

  const openClearDatesDialog = (school) => {
    setSelectedSchool(school);
    setShowClearDatesDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="school-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">School Date Management</h1>
          <p className="text-muted-foreground">Set start/end dates for schools and manage auto-archiving</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-sm">
            <Calendar className="w-4 h-4 mr-1" />
            Today: {currentDate}
          </Badge>
          <Button variant="outline" onClick={fetchSchools} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Schools with end dates will be <strong>automatically archived at midnight EST</strong> on the end date.
          Admins can manually archive before then using the "Archive Now" button. All enrollments, exam results, and attendance records will be moved to archive.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>All Schools</CardTitle>
          <CardDescription>Manage school cycles and archiving schedules</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>School Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Enrolled</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.map((school) => (
                <TableRow key={school.id} data-testid={`school-row-${school.id}`}>
                  <TableCell className="font-medium">{school.name}</TableCell>
                  <TableCell>{getStatusBadge(school.status)}</TableCell>
                  <TableCell>{school.start_date || <span className="text-muted-foreground">Not set</span>}</TableCell>
                  <TableCell>{school.end_date || <span className="text-muted-foreground">Not set</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      {school.enrolled_students}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {school.status !== 'completed' ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openSetDatesDialog(school)}
                            data-testid={`set-dates-btn-${school.id}`}
                          >
                            <Calendar className="w-4 h-4 mr-1" />
                            {school.has_dates_set ? 'Edit Dates' : 'Set Dates'}
                          </Button>
                          {school.has_dates_set && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openClearDatesDialog(school)}
                                data-testid={`clear-dates-btn-${school.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Clear Dates
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => openArchiveDialog(school)}
                                data-testid={`archive-btn-${school.id}`}
                              >
                                <Archive className="w-4 h-4 mr-1" />
                                Archive Now
                              </Button>
                            </>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openReopenDialog(school)}
                          data-testid={`reopen-btn-${school.id}`}
                        >
                          <PlayCircle className="w-4 h-4 mr-1" />
                          Reopen School
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Set/Edit Dates Dialog */}
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedSchool?.has_dates_set ? 'Edit School Dates' : 'Set School Dates'}
            </DialogTitle>
            <DialogDescription>
              Configure start and end dates for <strong>{selectedSchool?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={dateForm.start_date}
                onChange={(e) => setDateForm({ ...dateForm, start_date: e.target.value })}
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={dateForm.end_date}
                onChange={(e) => setDateForm({ ...dateForm, end_date: e.target.value })}
                data-testid="end-date-input"
              />
            </div>
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Data will be auto-archived at <strong>midnight EST</strong> on the end date. You can manually archive anytime before then.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDateDialog(false)}>Cancel</Button>
            <Button onClick={handleSetDates} disabled={processing} data-testid="save-dates-btn">
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive School Data</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive all data for <strong>{selectedSchool?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will archive:
              <ul className="list-disc list-inside mt-2">
                <li>All student enrollments ({selectedSchool?.enrolled_students} students)</li>
                <li>All exam results</li>
                <li>All attendance sessions and records</li>
              </ul>
              The school will be marked as "Completed" and can be reopened later.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleArchiveNow} disabled={processing} data-testid="confirm-archive-btn">
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Archive Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen School Dialog */}
      <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen School</DialogTitle>
            <DialogDescription>
              Set new dates to reopen <strong>{selectedSchool?.name}</strong> for a new cycle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reopen_start_date">New Start Date</Label>
              <Input
                id="reopen_start_date"
                type="date"
                value={dateForm.start_date}
                onChange={(e) => setDateForm({ ...dateForm, start_date: e.target.value })}
                data-testid="reopen-start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reopen_end_date">New End Date</Label>
              <Input
                id="reopen_end_date"
                type="date"
                value={dateForm.end_date}
                onChange={(e) => setDateForm({ ...dateForm, end_date: e.target.value })}
                data-testid="reopen-end-date-input"
              />
            </div>
            <Alert>
              <PlayCircle className="h-4 w-4" />
              <AlertDescription>
                Reopening the school will allow new registrations and enrollments. Previously archived data remains in the archive.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReopenDialog(false)}>Cancel</Button>
            <Button onClick={handleReopenSchool} disabled={processing} data-testid="confirm-reopen-btn">
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reopen School
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Dates Confirmation Dialog */}
      <Dialog open={showClearDatesDialog} onOpenChange={setShowClearDatesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear School Dates</DialogTitle>
            <DialogDescription>
              Are you sure you want to clear dates for <strong>{selectedSchool?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <Alert>
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              This will remove the start and end dates. The school will become <strong>active without date restrictions</strong> and will not be auto-archived.
              You can set new dates at any time.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDatesDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearDates} disabled={processing} data-testid="confirm-clear-dates-btn">
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Clear Dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function SchoolManagementPage() {
  return (
    <AdminProtected title="School Management">
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 pt-20">
          <SchoolManagementContent />
        </main>
        <Footer />
      </div>
    </AdminProtected>
  );
}
